/**
 * The floorplans of the bank's gallery view (the Galleria button): the
 * Piirustus facet, the Kartta map and the pin disc on the cards. Pure
 * helpers, shared by the filter, the selectors, the hook and the components,
 * so none of them has to reach into another for a rule.
 */

/**
 * The floorplan facet's value for the photos placed on no floorplan. The
 * old gallery's literal (FloorplanFilter), not imported from there: that
 * module pulls in the gallery's selectors and dialogs.
 */
export const NO_FLOORPLAN = 'NO_FLOORPLAN';

/**
 * How available the gallery view's map (Kartta) is: `none` outside the
 * gallery view, `loading` until the floorplans have loaded, `ready` with a
 * floorplan to show or for a user who may add one, `empty` for anyone else
 * on a project without floorplans, `failed` when the load failed.
 */
export const BANK_MAP_AVAILABILITY = {
  NONE: 'none',
  LOADING: 'loading',
  READY: 'ready',
  EMPTY: 'empty',
  FAILED: 'failed',
};

const isFiniteNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Whether the facet names one floorplan: not '' (every photo) and not
 * NO_FLOORPLAN.
 * @param {*} floorplanId bankFilters.floorplanId
 * @returns {boolean}
 */
export function isSpecificFloorplanId(floorplanId) {
  return (
    typeof floorplanId === 'string' &&
    floorplanId !== '' &&
    floorplanId !== NO_FLOORPLAN
  );
}

/**
 * Whether `area` is an area of a floorplan as the map draws it: `{x, y,
 * width, height}` in fractions of the image.
 * @param {*} area bankFilters.floorplanArea
 * @returns {boolean}
 */
export function isBankFloorplanArea(area) {
  return (
    !!area &&
    typeof area === 'object' &&
    isFiniteNumber(area.x) &&
    isFiniteNumber(area.y) &&
    isFiniteNumber(area.width) &&
    isFiniteNumber(area.height)
  );
}

/**
 * Whether two areas are the same one (null for none), so that a map that
 * reports its area again changes nothing.
 * @param {?Object} a
 * @param {?Object} b
 * @returns {boolean}
 */
export function isSameBankFloorplanArea(a, b) {
  if (!isBankFloorplanArea(a) || !isBankFloorplanArea(b)) {
    return !isBankFloorplanArea(a) && !isBankFloorplanArea(b);
  }
  return (
    a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
  );
}

/**
 * Whether a placement lies in the area, its edges included (as the old
 * gallery's rule).
 * @param {{x: number, y: number}} placement
 * @param {{x: number, y: number, width: number, height: number}} area
 * @returns {boolean}
 */
export function isPlacementInArea(placement, area) {
  if (!placement || !isBankFloorplanArea(area)) return false;
  return (
    placement.x >= area.x &&
    placement.x <= area.x + area.width &&
    placement.y >= area.y &&
    placement.y <= area.y + area.height
  );
}

const compareFloorplans = (a, b) =>
  String(a.name || '').localeCompare(String(b.name || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  }) || String(a.id || '').localeCompare(String(b.id || ''));

/**
 * The floorplans in the order the bank lists them: by name, numbers in
 * their numeric order ("Kerros 2" before "Kerros 10") and letters without
 * regard to case; same names by id, so the order never changes on a reload.
 * @param {Array<Object>} floorplans
 * @returns {Array<Object>} a new, sorted array
 */
export function sortBankFloorplans(floorplans) {
  return (Array.isArray(floorplans) ? floorplans : [])
    .filter(Boolean)
    .sort(compareFloorplans);
}

/**
 * The floorplan the map shows when nothing specific is chosen: `preferredId`
 * while it is still one of the floorplans (the one the map showed last),
 * else the first of them, else '' (there is none to show).
 * @param {Array<{id: string}>} floorplans sorted (sortBankFloorplans)
 * @param {?string} [preferredId]
 * @returns {string}
 */
export function pickBankMapFloorplanId(floorplans, preferredId) {
  const list = Array.isArray(floorplans) ? floorplans : [];
  if (
    isSpecificFloorplanId(preferredId) &&
    list.some((floorplan) => floorplan.id === preferredId)
  ) {
    return preferredId;
  }
  return list.length > 0 ? list[0].id : '';
}

/**
 * How available the gallery view's map is (BANK_MAP_AVAILABILITY).
 * @param {Object} options
 * @param {boolean} options.inScope the gallery view of the public bank
 * @param {string} options.status the bank's floorplan load: 'idle', 'loading', 'loaded', 'failed' or 'stale'
 * @param {number} options.floorplanCount the project's floorplans
 * @param {boolean} options.canManage the user may add floorplans
 * @returns {string} one of BANK_MAP_AVAILABILITY
 */
export function getBankMapAvailability({
  inScope,
  status,
  floorplanCount,
  canManage,
}) {
  if (!inScope) return BANK_MAP_AVAILABILITY.NONE;
  if (status === 'failed') return BANK_MAP_AVAILABILITY.FAILED;
  // Idle, loading, or stale (the data may have been replaced by another
  // project's or wiped, see the slice): what is in the store is not the
  // project's yet.
  if (status !== 'loaded') return BANK_MAP_AVAILABILITY.LOADING;
  return floorplanCount > 0 || canManage
    ? BANK_MAP_AVAILABILITY.READY
    : BANK_MAP_AVAILABILITY.EMPTY;
}

/**
 * What the floorplan facet adds to the active filter count: 1 or 0, never
 * more, whatever of it is on. An area counts. A floorplan counts in the grid
 * and the list, where it narrows the photos, but not in the map: there it is
 * what the user is looking at, which "Tyhjennä" keeps. Nothing counts
 * outside the gallery view, and nothing without options.
 * @param {Object} filters bankFilters
 * @param {{inScope?: boolean, isMapView?: boolean}} [options]
 * @returns {number} 0 or 1
 */
export function getBankFloorplanFacetCount(filters, options = {}) {
  const { inScope = false, isMapView = false } = options || {};
  if (!inScope || !filters) return 0;
  const { floorplanId, floorplanArea } = filters;
  if (
    isSpecificFloorplanId(floorplanId) &&
    isBankFloorplanArea(floorplanArea)
  ) {
    return 1;
  }
  if (isMapView) return 0;
  return floorplanId === NO_FLOORPLAN || isSpecificFloorplanId(floorplanId)
    ? 1
    : 0;
}
